import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Music, Mail, ShieldCheck, Search } from "lucide-react";
import { publicApi, errMsg } from "@/lib/api";

// ----------------------------------------------------------------------
// GFMP Sync Catalogue · /gfmp/sync-catalogue
// Public-facing surface. Strict additive layer over the existing GFMP
// brand language. Cards display ONLY approved public fields returned by
// /api/public/gfmp/sync-catalogue. No authentication, no private data,
// no royalty/legal/contract surfaces.
// ----------------------------------------------------------------------

// Brand tokens — copied verbatim from GFMP.jsx so this surface remains
// visually congruent without coupling to that file.
const INK     = "hsl(20 14% 6%)";
const INK_2   = "hsl(20 12% 9%)";
const CREAM   = "hsl(36 33% 96%)";
const CREAM_2 = "hsl(36 22% 93%)";
const BURG    = "hsl(347 60% 22%)";
const GOLD    = "hsl(40 48% 58%)";
const CHAR    = "hsl(20 10% 16%)";

function PillButton({ to, children, variant = "solid", external = false, dataTestId, onClick }) {
  const baseStyle =
    variant === "solid"
      ? { background: GOLD, color: INK, border: `1px solid ${GOLD}` }
      : variant === "outline"
      ? { background: "transparent", color: CREAM, border: `1px solid ${GOLD}80` }
      : { background: "transparent", color: GOLD, border: `1px solid ${GOLD}33` };
  const inner = (
    <span
      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[12px] uppercase tracking-[0.22em] transition-opacity hover:opacity-90"
      style={baseStyle}
      data-testid={dataTestId}
    >
      {children}
    </span>
  );
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className="focus:outline-none">
        {inner}
      </button>
    );
  }
  if (external) {
    return (
      <a href={to} target="_blank" rel="noreferrer" className="focus:outline-none">
        {inner}
      </a>
    );
  }
  return (
    <Link to={to} className="focus:outline-none">
      {inner}
    </Link>
  );
}

function CardArtwork() {
  // Placeholder artwork tile. No external assets — a deliberate gold-on-burgundy
  // gradient with a small monogram so the catalogue never looks under-dressed
  // even when approved artwork has not yet been added.
  return (
    <div
      className="relative w-full aspect-square rounded-lg overflow-hidden"
      style={{
        background:
          `radial-gradient(120% 100% at 20% 20%, ${BURG}, ${INK_2} 75%)`,
        border: `1px solid ${GOLD}33`,
      }}
      aria-hidden="true"
    >
      <span
        className="absolute inset-0 flex items-center justify-center font-display tracking-[0.18em] text-[11px] uppercase"
        style={{ color: `${GOLD}99` }}
      >
        GFMP · Sync
      </span>
      <span
        className="absolute bottom-3 right-3 inline-block w-1.5 h-1.5 rounded-full"
        style={{ background: GOLD, boxShadow: `0 0 0 4px ${GOLD}22` }}
      />
    </div>
  );
}

function CatalogueCard({ item, onRequest }) {
  return (
    <article
      data-testid={`gfmp-sync-card-${item.public_track_id}`}
      className="rounded-xl overflow-hidden flex flex-col h-full transition-transform hover:-translate-y-0.5"
      style={{ background: INK_2, border: `1px solid ${GOLD}1f` }}
    >
      <div className="p-3.5 pb-0">
        <CardArtwork />
      </div>
      <div className="px-4 pt-4 pb-3 flex-1 flex flex-col">
        <h3
          className="font-display text-[19px] leading-tight tracking-tight"
          style={{ color: CREAM }}
          data-testid="gfmp-sync-card-title"
        >
          {item.title}
        </h3>
        {item.artist_or_project && (
          <div
            className="mt-1 text-[12px] uppercase tracking-[0.18em]"
            style={{ color: `${GOLD}cc` }}
            data-testid="gfmp-sync-card-artist"
          >
            {item.artist_or_project}
          </div>
        )}
        {item.genre && (
          <div className="mt-3 text-[12px]" style={{ color: `${CREAM}99` }}>
            {item.genre}
          </div>
        )}
        {item.mood_tags?.length > 0 && (
          <ul className="mt-3 flex flex-wrap gap-1.5" data-testid="gfmp-sync-card-tags">
            {item.mood_tags.slice(0, 6).map((m) => (
              <li
                key={m}
                className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full"
                style={{ color: `${CREAM}cc`, border: `1px solid ${CREAM}22` }}
              >
                {m}
              </li>
            ))}
          </ul>
        )}
        <ul className="mt-4 space-y-1 text-[12px]" style={{ color: `${CREAM}b3` }}>
          {item.instrumental_available && (
            <li className="flex items-center gap-2">
              <span className="w-1 h-1 rounded-full" style={{ background: GOLD }} />
              Instrumental available
            </li>
          )}
          {item.clean_available && (
            <li className="flex items-center gap-2">
              <span className="w-1 h-1 rounded-full" style={{ background: GOLD }} />
              Clean version available
            </li>
          )}
          {item.one_stop_or_controlled && (
            <li className="flex items-center gap-2">
              <span className="w-1 h-1 rounded-full" style={{ background: GOLD }} />
              One-stop / controlled-rights indicated
            </li>
          )}
          {item.preview_available && (
            <li className="flex items-center gap-2 italic" style={{ color: `${CREAM}66` }}>
              Stream-only preview prepared on request
            </li>
          )}
        </ul>
        {item.description && (
          <p
            className="mt-4 text-[12.5px] leading-relaxed"
            style={{ color: `${CREAM}a8` }}
            data-testid="gfmp-sync-card-description"
          >
            {item.description}
          </p>
        )}
        <div className="mt-auto pt-5 flex items-center justify-between gap-3">
          <span
            className="text-[10.5px] uppercase tracking-[0.22em]"
            style={{ color: `${GOLD}aa` }}
          >
            Rights status on request
          </span>
          <button
            type="button"
            onClick={() => onRequest(item)}
            className="inline-flex items-center gap-1.5 text-[12px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-full transition-opacity hover:opacity-85"
            style={{ background: GOLD, color: INK }}
            data-testid={`gfmp-sync-card-request-${item.public_track_id}`}
          >
            Request Information
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </article>
  );
}

function EmptyState() {
  return (
    <div
      className="rounded-xl px-6 py-12 sm:py-16 text-center"
      style={{ background: INK_2, border: `1px dashed ${GOLD}33` }}
      data-testid="gfmp-sync-empty-state"
    >
      <Music className="w-7 h-7 mx-auto mb-4" style={{ color: `${GOLD}cc` }} />
      <p className="text-[15px] leading-relaxed max-w-xl mx-auto" style={{ color: `${CREAM}cc` }}>
        Selected catalogue entries are being prepared for licensing review.
        Contact the GFMP Sync Desk for current availability.
      </p>
      <div className="mt-6">
        <PillButton
          to="/gfmp/sync-catalogue/inquiry"
          variant="solid"
          dataTestId="gfmp-sync-empty-cta"
        >
          Contact the GFMP Sync Desk
        </PillButton>
      </div>
    </div>
  );
}

export default function GFMPSyncCatalogue() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQ, setSearchQ] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [error, setError] = useState("");

  async function fetchCatalogue(q = "") {
    setLoading(true);
    setError("");
    try {
      const r = await publicApi.get("/public/gfmp/sync-catalogue", {
        params: q ? { q } : {},
      });
      setItems(Array.isArray(r.data?.items) ? r.data.items : []);
    } catch (e) {
      setItems([]);
      setError(errMsg(e, "Unable to load catalogue. Please try again."));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchCatalogue();
  }, []);

  function submitSearch(e) {
    e?.preventDefault?.();
    const q = (searchInput || "").trim();
    setSearchQ(q);
    fetchCatalogue(q);
  }

  function clearSearch() {
    setSearchInput("");
    setSearchQ("");
    fetchCatalogue("");
  }

  function goToInquiry(item) {
    const params = new URLSearchParams();
    if (item) {
      params.set("track", item.public_track_id);
      params.set("title", item.title || "");
    }
    navigate(`/gfmp/sync-catalogue/inquiry${params.toString() ? `?${params.toString()}` : ""}`);
  }

  const hasResults = items.length > 0;
  const resultsLabel = useMemo(() => {
    if (loading) return "Loading…";
    if (searchQ) return `${items.length} match${items.length === 1 ? "" : "es"} for “${searchQ}”`;
    return `${items.length} entr${items.length === 1 ? "y" : "ies"} prepared for review`;
  }, [loading, items.length, searchQ]);

  return (
    <div
      data-testid="gfmp-sync-catalogue-page"
      className="min-h-screen w-full flex flex-col"
      style={{ background: INK, color: CREAM }}
    >
      {/* ============================================================
          Top navigation row — return to /gfmp without leaving brand
          ============================================================ */}
      <header
        className="w-full"
        style={{ borderBottom: `1px solid ${GOLD}26`, background: INK }}
      >
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-5 flex items-center justify-between gap-4">
          <Link
            to="/gfmp"
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] transition-opacity hover:opacity-80"
            style={{ color: `${CREAM}cc` }}
            data-testid="gfmp-sync-back-to-gfmp"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Good Friar Music Publishing
          </Link>
          <span
            className="text-[10px] uppercase tracking-[0.22em] hidden sm:inline-block"
            style={{ color: `${GOLD}aa` }}
          >
            Sync Catalogue
          </span>
        </div>
      </header>

      {/* ============================================================
          1) HERO — sets restrained tone + required copy
          ============================================================ */}
      <section
        className="relative w-full"
        data-testid="gfmp-sync-hero"
        style={{
          background:
            "radial-gradient(900px 500px at 50% 0%, hsl(347 60% 22% / 0.22), transparent 60%)," + INK,
        }}
      >
        <div className="max-w-5xl w-full mx-auto px-6 sm:px-10 pt-14 sm:pt-20 pb-12 sm:pb-16 text-center">
          <div
            className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.24em]"
            style={{ color: GOLD }}
            data-testid="gfmp-sync-eyebrow"
          >
            <span style={{ width: 18, height: 1, background: GOLD }} aria-hidden="true" />
            For Music Supervisors & Licensing
          </div>
          <h1
            className="font-display mt-5 text-[34px] sm:text-[44px] md:text-[52px] leading-[1.05] tracking-tight"
            data-testid="gfmp-sync-headline"
            style={{ color: CREAM }}
          >
            GFMP{" "}
            <span style={{ color: GOLD }}>Sync Catalogue</span>
          </h1>
          <p
            className="mt-5 text-[15px] sm:text-[17px] leading-relaxed max-w-2xl mx-auto"
            data-testid="gfmp-sync-subheadline"
            style={{ color: `${CREAM}cc` }}
          >
            Music prepared for professional licensing review.
          </p>
          <p
            className="mt-4 text-[13px] sm:text-[14px] leading-relaxed max-w-2xl mx-auto"
            data-testid="gfmp-sync-intro"
            style={{ color: `${CREAM}99` }}
          >
            Good Friar Music Publishing represents and coordinates selected music works and
            recordings for licensing consideration. Catalogue availability, rights status, and
            clearance details are confirmed upon request.
          </p>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <PillButton
              to="#catalogue"
              variant="outline"
              dataTestId="gfmp-sync-cta-browse"
              onClick={() => {
                const el = document.getElementById("catalogue");
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
            >
              Browse the Sync Catalogue
            </PillButton>
            <PillButton
              to="/gfmp/sync-catalogue/inquiry"
              variant="solid"
              dataTestId="gfmp-sync-cta-request-info"
            >
              Request Licensing Information
            </PillButton>
            <PillButton
              to="/gfmp/sync-catalogue/inquiry?desk=1"
              variant="ghost"
              dataTestId="gfmp-sync-cta-sync-desk"
            >
              <Mail className="w-3.5 h-3.5" /> Contact the GFMP Sync Desk
            </PillButton>
          </div>

          <div
            className="mt-8 inline-flex items-center gap-3 px-4 py-2 rounded-full"
            data-testid="gfmp-sync-rights-chip"
            style={{
              background: "transparent",
              border: `1px solid ${GOLD}55`,
              color: `${CREAM}d6`,
            }}
          >
            <ShieldCheck className="w-3.5 h-3.5" style={{ color: GOLD }} />
            <span className="text-[10.5px] sm:text-[11.5px] uppercase tracking-[0.22em]">
              Rights status available upon request
            </span>
          </div>
        </div>
      </section>

      {/* ============================================================
          2) CATALOGUE GRID — cards or empty-state
          ============================================================ */}
      <section
        id="catalogue"
        className="w-full"
        style={{ background: INK }}
        data-testid="gfmp-sync-grid-section"
      >
        <div className="max-w-6xl w-full mx-auto px-6 sm:px-10 py-12 sm:py-16">
          {/* Search row */}
          <div className="flex flex-wrap items-center gap-3 justify-between mb-6">
            <div className="flex items-baseline gap-3">
              <div
                className="text-[10px] uppercase tracking-[0.24em]"
                style={{ color: GOLD }}
              >
                Catalogue
              </div>
              <div
                className="text-[12px] uppercase tracking-[0.18em]"
                style={{ color: `${CREAM}99` }}
                data-testid="gfmp-sync-results-label"
              >
                {resultsLabel}
              </div>
            </div>
            <form
              onSubmit={submitSearch}
              className="flex items-center gap-2"
              data-testid="gfmp-sync-search-form"
            >
              <div
                className="flex items-center gap-2 px-3 py-1.5 rounded-full"
                style={{ background: INK_2, border: `1px solid ${GOLD}26` }}
              >
                <Search className="w-3.5 h-3.5" style={{ color: `${CREAM}99` }} />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search title, artist, or genre…"
                  className="bg-transparent outline-none text-[12px] w-[210px] sm:w-[280px]"
                  style={{ color: CREAM }}
                  aria-label="Search catalogue"
                  data-testid="gfmp-sync-search-input"
                />
                {searchInput && (
                  <button
                    type="button"
                    onClick={clearSearch}
                    className="text-[10px] uppercase tracking-wide opacity-70 hover:opacity-100"
                    style={{ color: `${CREAM}99` }}
                    data-testid="gfmp-sync-search-clear"
                  >
                    Clear
                  </button>
                )}
              </div>
              <button
                type="submit"
                className="text-[11px] uppercase tracking-[0.22em] px-3.5 py-1.5 rounded-full"
                style={{ background: GOLD, color: INK }}
                data-testid="gfmp-sync-search-submit"
              >
                Search
              </button>
            </form>
          </div>

          {error && (
            <div
              role="alert"
              className="mb-6 rounded-md px-4 py-3 text-[12.5px]"
              style={{ background: `${BURG}33`, border: `1px solid ${BURG}66`, color: CREAM }}
              data-testid="gfmp-sync-error"
            >
              {error}
            </div>
          )}

          {loading ? (
            <div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
              data-testid="gfmp-sync-grid-loading"
            >
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="rounded-xl overflow-hidden"
                  style={{ background: INK_2, border: `1px solid ${GOLD}1f` }}
                >
                  <div className="aspect-square w-full" style={{ background: `${INK}99` }} />
                  <div className="p-4 space-y-2">
                    <div className="h-4 w-1/2 rounded" style={{ background: `${CREAM}1a` }} />
                    <div className="h-3 w-1/3 rounded" style={{ background: `${CREAM}14` }} />
                    <div className="h-3 w-3/4 rounded" style={{ background: `${CREAM}10` }} />
                  </div>
                </div>
              ))}
            </div>
          ) : hasResults ? (
            <div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
              data-testid="gfmp-sync-grid"
            >
              {items.map((it) => (
                <CatalogueCard key={it.public_track_id} item={it} onRequest={goToInquiry} />
              ))}
            </div>
          ) : (
            <EmptyState />
          )}
        </div>
      </section>

      {/* ============================================================
          3) NOTICE — explicit non-licence framing
          ============================================================ */}
      <section
        className="w-full"
        style={{ background: CHAR, color: CREAM }}
        data-testid="gfmp-sync-notice-section"
      >
        <div className="max-w-5xl mx-auto px-6 sm:px-10 py-14 sm:py-18">
          <div
            className="rounded-2xl px-6 sm:px-8 py-7"
            style={{ background: INK_2, border: `1px solid ${GOLD}33` }}
          >
            <div
              className="text-[10px] uppercase tracking-[0.24em] mb-3"
              style={{ color: GOLD }}
            >
              Important
            </div>
            <p className="text-[13px] sm:text-[14px] leading-relaxed" style={{ color: `${CREAM}cc` }}>
              Browsing this catalogue does not grant any licence, usage right, quote, clearance
              confirmation, or permission to use any music. Every request remains subject to rights
              review, availability, written approval, and a fully executed licence agreement.
            </p>
            <div className="mt-5">
              <PillButton
                to="/gfmp/sync-catalogue/inquiry"
                variant="solid"
                dataTestId="gfmp-sync-notice-cta"
              >
                Submit a Licensing Inquiry
              </PillButton>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          4) FOOTER — minimal, brand-line
          ============================================================ */}
      <footer
        className="w-full mt-auto"
        style={{ background: INK_2, color: `${CREAM}99`, borderTop: `1px solid ${GOLD}26` }}
        data-testid="gfmp-sync-footer"
      >
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-7 flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-col">
            <span className="font-display text-[16px]" style={{ color: CREAM }}>
              Good Friar Music Publishing
            </span>
            <span className="text-[10px] uppercase tracking-[0.22em]" style={{ color: `${GOLD}aa` }}>
              A Durban 49 Group Company
            </span>
          </div>
          <div className="flex items-center gap-4">
            <Link
              to="/gfmp"
              className="text-[11px] uppercase tracking-[0.22em] hover:opacity-80"
              style={{ color: `${CREAM}cc` }}
              data-testid="gfmp-sync-footer-back"
            >
              Back to GFMP
            </Link>
            <Link
              to="/d49"
              className="text-[11px] uppercase tracking-[0.22em] hover:opacity-80"
              style={{ color: `${CREAM}cc` }}
              data-testid="gfmp-sync-footer-d49"
            >
              The Durban 49 Group
            </Link>
          </div>
        </div>
        <div
          className="max-w-6xl mx-auto px-6 sm:px-10 pb-6 text-[11px]"
          style={{ color: `${CREAM_2}66` }}
        >
          © {new Date().getFullYear()} Good Friar Music Publishing. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
